/* eslint-disable react/no-unstable-nested-components */
import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Button, Input, Card, CardBody } from 'reactstrap';
import { toast } from 'react-toastify';
// eslint-disable-next-line import/no-extraneous-dependencies
import SketchPicker from 'react-color/lib/components/sketch/Sketch';
import Loader from 'components/common/Loader';
import DeleteConfirmationAlert from 'components/DeleteConfirmationAlert';
import { FSBTable } from 'components/common/FSBTable';
import {
  GET_ALL_TRADER_TAGS,
  CREATE_TRADER_TAG,
  UPDATE_TRADER_TAG,
  DELETE_TRADER_TAG,
} from '../../SettingsGraphQL';
import './style.css';

const FlagUI = () => {
  const [color, setColor] = useState('#000000');
  const [tagName, setTagName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editColor, setEditColor] = useState('');
  const [editTagName, setEditTagName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    id: null,
  });

  const { data, loading, error, refetch } = useQuery(GET_ALL_TRADER_TAGS, {
    fetchPolicy: 'network-only',
  });

  const [createTraderTag, { loading: createLoading }] = useMutation(
    CREATE_TRADER_TAG,
    {
      onCompleted: (response) => {
        toast.success(
          response?.createTraderTag || 'Trader tag created successfully.',
        );
        setTagName('');
        setColor('#000000');
        refetch();
      },
      onError: (err) => {
        toast.error(
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          'Unable to create trader tag.',
        );
      },
    },
  );

  const [updateTraderTag, { loading: updateLoading }] = useMutation(
    UPDATE_TRADER_TAG,
    {
      onCompleted: () => {
        toast.success('Trader tag updated successfully.');
        setEditingId(null);
        refetch();
      },
      onError: (err) => {
        toast.error(
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          'Unable to update trader tag.',
        );
      },
    },
  );

  const [deleteTraderTag, { loading: deleteLoading }] = useMutation(
    DELETE_TRADER_TAG,
    {
      onCompleted: () => {
        toast.success('Trader tag deleted successfully.');
        setDeleteConfirm({ open: false, id: null });
        refetch();
      },
      onError: (err) => {
        toast.error(
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          'Unable to delete trader tag.',
        );
      },
    },
  );

  const handleSave = async () => {
    const trimmedName = tagName.trim();
    if (!trimmedName) {
      toast.error('Please enter a tag name.');
      return;
    }
    await createTraderTag({
      variables: {
        input: {
          tagName: trimmedName,
          colorCode: color,
        },
      },
    });
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirm({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    await deleteTraderTag({
      variables: {
        traderTagId: deleteConfirm.id,
      },
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, id: null });
  };

  const handleEditStart = (tag) => {
    setEditingId(tag.id);
    setEditColor(tag.colorCode || '#000000');
    setEditTagName(tag.tagName || '');
  };

  const handleEditCancel = useCallback(() => {
    setEditingId(null);
    setEditColor('');
    setEditTagName('');
  }, []);

  const handleEditSave = useCallback(async () => {
    const trimmedName = editTagName.trim();
    if (!trimmedName) {
      toast.error('Please enter a tag name.');
      return;
    }
    await updateTraderTag({
      variables: {
        input: {
          id: parseInt(editingId, 10),
          tagName: trimmedName,
          colorCode: editColor,
        },
      },
    });
  }, [editTagName, editingId, editColor, updateTraderTag]);

  const flagUIColumns = useMemo(
    () => [
      {
        accessorKey: 'colorCode',
        header: 'COLOR CODE',
        enableSorting: false,
        size: 150,
        cell: ({ row }) => {
          const tag = row.original;
          if (editingId === tag.id) {
            return (
              <div className="flag-ui-color-cell">
                <input
                  aria-label="Edit Color"
                  type="color"
                  className="flag-ui-edit-color-input"
                  value={editColor}
                  onChange={(e) => setEditColor(e.target.value)}
                />
                <span className="flag-ui-color-code">{editColor}</span>
              </div>
            );
          }
          return (
            <div className="flag-ui-color-cell">
              <span
                className="flag-ui-color-swatch"
                style={{ backgroundColor: tag.colorCode }}
              />
              <span className="flag-ui-color-code">{tag.colorCode}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'tagName',
        header: 'TAG',
        enableSorting: false,
        cell: ({ row }) => {
          const tag = row.original;
          if (editingId === tag.id) {
            return (
              <Input
                aria-label="Edit Tag Name"
                type="text"
                className="flag-ui-edit-input"
                value={editTagName}
                onChange={(e) => setEditTagName(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                autoFocus
              />
            );
          }
          return tag.tagName || '-';
        },
      },
      {
        accessorKey: 'actions',
        header: 'ACTIONS',
        enableSorting: false,
        size: 120,
        cell: ({ row }) => {
          const tag = row.original;
          if (editingId === tag.id) {
            return (
              <div className="flag-ui-edit-actions">
                <button
                  aria-label="Save Edit"
                  type="button"
                  className="flag-ui-edit-save-btn"
                  onClick={handleEditSave}
                  disabled={updateLoading}
                  title="Save"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </button>
                <button
                  aria-label="Cancel Edit"
                  type="button"
                  className="flag-ui-edit-cancel-btn"
                  onClick={handleEditCancel}
                  title="Cancel"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            );
          }
          return (
            <div className="flag-ui-edit-actions">
              <button
                aria-label="Edit Tag"
                type="button"
                className="flag-ui-action-btn edit-btn"
                onClick={() => handleEditStart(tag)}
                title="Update"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
              <button
                aria-label="Delete Tag"
                type="button"
                className="flag-ui-action-btn delete-btn"
                onClick={() => handleDeleteClick(tag.id)}
                title="Delete"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            </div>
          );
        },
      },
    ],
    [
      editingId,
      editColor,
      editTagName,
      handleEditSave,
      updateLoading,
      handleEditCancel,
    ],
  );

  const tags = data?.getAllTraderTags?.filter((tag) => !tag.isDeleted) || [];

  if (loading) return <Loader />;
  if (error)
    return (
      <div>
        There is an error on this page : {error.message}
      </div>
    );

  return (
    <div className="flag-ui-container">
      {/* Delete Confirmation Popup */}
      <DeleteConfirmationAlert
        open={deleteConfirm.open}
        title="Delete"
        message="Do you really want to remove this record?"
        cancelButtonLabel="Close"
        addButtonLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        toggleAlert={handleDeleteCancel}
        loading={deleteLoading}
      />

      {/* Form Section - Color picker left, right column with preview and input */}
      <div className="flag-ui-form-section">
        <div className="flag-ui-picker-row">
          {/* Left side: Color Picker */}
          <div className="flag-ui-color-picker-wrapper">
            <label className="form-label" htmlFor="flag-ui-color">
              Color
            </label>
            <SketchPicker
              color={color}
              onChange={(newColor) => setColor(newColor.hex)}
              disableAlpha
              presetColors={['#FF0000', '#DC3545', '#007BFF', '#28A745', '#FFC107', '#17A2B8', '#343A40', '#6C757D', '#000000']}
            />
          </div>

          {/* Right side: Preview, Tag Input, Save Button */}
          <div className="flag-ui-right-column">
            <div className="flag-ui-color-preview-wrapper">
              <label className="form-label">Selected Color</label>
              <div
                className="flag-ui-color-preview"
                style={{ backgroundColor: color }}
              />
              <span className="flag-ui-color-preview-code">{color}</span>
            </div>

            <div className="flag-ui-text-wrapper">
              <label className="form-label" htmlFor="flag-ui-text">
                Tag Name
              </label>
              <Input
                id="flag-ui-text"
                type="text"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                placeholder="Enter tag name..."
                disabled={createLoading}
              />
            </div>

            <div className="flag-ui-save-btn">
              <Button
                color="primary"
                size="sm"
                onClick={handleSave}
                disabled={createLoading}
              >
                {createLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="flag-ui-table-container">
        <Card className="border-0 card-box page-card-box">
          <CardBody>
            <FSBTable
              data={tags}
              columns={flagUIColumns}
              keyField="id"
              showSearch={false}
              showFilters={false}
              showDelete={false}
              showPagination={false}
              striped={false}
              hover={false}
              bordered={false}
              wrapperClass="table-responsive word-break"
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default FlagUI;
